// src/components/Tutorial/Examples/LeaderboardCard.js
import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Stack, 
  Avatar, 
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import { Star as StarIcon, RateReview as ReviewIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';

export default function LeaderboardCard() {
  const theme = useTheme();
  const topCreators = [
    { id: 1, username: 'TopCreator', averageQuality: 4.8, numReviews: 127, score: 1245, profilePicture: null },
    { id: 2, username: 'GreatReviewer', averageQuality: 4.6, numReviews: 89, score: 892, profilePicture: null },
    { id: 3, username: 'AwesomeReviewer', averageQuality: 4.7, numReviews: 64, score: 756, profilePicture: null },
  ];

  return (
    <Box className="example-container" sx={{ mt: 3 }}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        {topCreators.map((c, idx) => (
          <Card
            key={c.id}
            elevation={0}
            sx={{
              flex: { xs: '0 0 100%', sm: '1 1 0' },
              minWidth: 0,
              borderRadius: 3,
              border: `2px solid ${idx === 0 
                ? '#FFD700'
                : idx === 1
                ? '#C0C0C0'
                : '#CD7F32'}`,
              background: 'white',
              p: 3,
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
            }}
          >
            {/* Rank Badge on Avatar */}
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 2.5 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  border: `4px solid ${idx === 0 
                    ? '#FFD700'
                    : idx === 1
                    ? '#C0C0C0'
                    : '#CD7F32'}`,
                  boxShadow: `0 6px 20px ${alpha(idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : '#CD7F32', 0.4)}`,
                }}
              >
                {c.username?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: idx === 0 
                    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                    : idx === 1
                    ? 'linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)'
                    : 'linear-gradient(135deg, #CD7F32 0%, #B87333 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`,
                  border: '3px solid white',
                }}
              >
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, fontSize: '1.25rem' }}>
                  {idx + 1}
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5, color: 'text.primary', fontSize: '1.2rem' }}>
              {c.username}
            </Typography>

            {/* Stats */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                <StarIcon sx={{ fontSize: 20, color: '#FFB800' }} />
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {c.averageQuality.toFixed(1)} / 5
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                <ReviewIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
                <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                  {c.numReviews} {c.numReviews === 1 ? 'review' : 'reviews'}
                </Typography>
              </Box>
              <Box sx={{ 
                mt: 'auto',
                pt: 2,
                px: 2.5,
                py: 1.25,
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mx: 'auto',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}>
                <TrophyIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                  {c.score.toFixed(1)}
                </Typography>
              </Box>
            </Box>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
